import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { FiMail, FiLock, FiShield, FiLogIn, FiArrowRight } from 'react-icons/fi';
import toast from 'react-hot-toast';

export default function LoginForm() {
  const { login, verify2FA: verify2faFn, resend2FA } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('login');
  const [challengeToken, setChallengeToken] = useState(null);
  const [emailHint, setEmailHint] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.require2FA) {
        setChallengeToken(result.challengeToken);
        setEmailHint(result.emailHint || '');
        setStep('2fa');
        return;
      }
      toast.success('Signed in successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handle2FA = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verify2faFn(challengeToken, twoFactorCode.trim());
      toast.success('Signed in successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      const data = await resend2FA(challengeToken);
      if (data.challengeToken) setChallengeToken(data.challengeToken);
      setTwoFactorCode('');
      toast.success('A new code is on its way');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Could not resend the code');
    }
  };

  const render2FA = () => (
    <form onSubmit={handle2FA} className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <FiShield className="text-foreground" size={20} />
        <h3 className="text-lg font-semibold tracking-tight text-foreground">Two-Factor Auth</h3>
      </div>
      <p className="text-muted-foreground text-sm">
        We emailed a 6-digit code{emailHint ? ` to ${emailHint}` : ''}. Enter it below, or use one of your recovery codes.
      </p>

      <div className="space-y-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground">Verification Code</label>
        <input
          type="text"
          maxLength={24}
          inputMode="text"
          autoComplete="one-time-code"
          placeholder="000000"
          value={twoFactorCode}
          onChange={(e) => setTwoFactorCode(e.target.value)}
          className="flex h-12 w-full rounded-md border border-input bg-background px-3 py-2 text-center text-2xl tracking-[0.5em] ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          required
          autoFocus
        />
      </div>
      
      <button 
        type="submit" 
        disabled={loading}
        className="inline-flex w-full items-center justify-center rounded-md bg-primary h-10 px-4 py-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
        ) : (
          <>Verify & Sign In <FiArrowRight size={16} /></>
        )}
      </button>

      <button
        type="button"
        onClick={handleResend}
        disabled={loading}
        className="w-full text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline disabled:opacity-50"
      >
        Didn&apos;t get it? Send a new code
      </button>
    </form>
  );

  const renderLogin = () => (
    <form onSubmit={handleLogin} className="space-y-4" autoComplete="off">
      <div className="space-y-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground">Email Address</label>
        <div className="relative">
          <FiMail className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="email"
            placeholder="admin@manager.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            required
            autoFocus
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <label className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-foreground">Password</label>
        <div className="relative">
          <FiLock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={16} />
          <input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="flex h-10 w-full rounded-md border border-input bg-background pl-9 pr-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            required
            autoComplete="new-password"
          />
        </div>
      </div>
      
      <button 
        type="submit" 
        disabled={loading}
        className="inline-flex w-full items-center justify-center rounded-md bg-primary h-10 px-4 py-2 mt-2 text-sm font-medium text-primary-foreground ring-offset-background transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 gap-2"
      >
        {loading ? (
          <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin"></div>
        ) : (
          <>Sign In</>
        )}
      </button>
    </form>
  );

  return (
    <>
      {step === '2fa' ? render2FA() : renderLogin()}
      <div className="mt-6 text-center text-xs text-muted-foreground">
        &copy; {new Date().getFullYear()} Panel Manager. All rights reserved.
      </div>
    </>
  );
}
